import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Button, Table, Label, Card } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import styled from 'styled-components'
import { PageHeader, LoaderBar, FlexBox } from 'components'
import { PaginationComponent } from 'components/Pagination'
import { AppPrimaryButton } from 'elements'
import { GetAuditCompanyList } from 'hooks'
import { useNotification } from 'hooks/toast'
import { useTabletAndBelowMediaQuery } from 'layout'
import { ImpersonationService } from 'utility'

const PAGE_SIZE = 20

const Filters = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: flex-end;
    margin-bottom: 16px;
    .field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 180px;
        ${({ $isMobile }) => $isMobile && 'min-width: 100%;'}
    }
    label {
        font-size: 12px;
        color: #6b7280;
        font-weight: 500;
    }
    input[type='date'] {
        padding: 8px 10px;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        font-size: 13px;
        min-height: 38px;
    }
`

const FooterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    font-size: 12px;
    color: #6b7280;
    flex-wrap: wrap;
    gap: 8px;
`

const CardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;

    .label {
        font-weight: 600;
        color: #334155;
        min-width: 110px;
        flex-shrink: 0;
    }

    .value {
        color: #0f172a;
        font-weight: 500;
        word-break: break-word;
    }
`

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;

    .ui.table {
        min-width: 820px;
    }
`

// Defensive accessors: the exact history field names can differ by environment.
const rowValue = (row, keys, fallback = '') => {
    const match = keys.find(
        key => row[key] !== undefined && row[key] !== null && row[key] !== '',
    )
    return match ? row[match] : fallback
}

const STATUS_MAP = {
    active: { color: 'green', text: 'Active' },
    live: { color: 'green', text: 'Active' },
    manual: { color: 'grey', text: 'Manual' },
    ended: { color: 'grey', text: 'Ended' },
    hardcap30: { color: 'red', text: 'HardCap30' },
    hardcap: { color: 'red', text: 'HardCap30' },
    expired: { color: 'red', text: 'Expired' },
    idle: { color: 'red', text: 'Idle timeout' },
    forced: { color: 'yellow', text: 'Forced by admin' },
    forcedbyadmin: { color: 'yellow', text: 'Forced by admin' },
}

const getStatusMeta = status => {
    const key = String(status || '')
        .toLowerCase()
        .replace(/[\s_-]/g, '')
    return STATUS_MAP[key] || { color: 'grey', text: status || 'Unknown' }
}

const isActiveStatus = status => {
    const key = String(status || '').toLowerCase()
    return key.includes('active') || key.includes('live')
}

const pad2 = value => String(value).padStart(2, '0')

const toLocalDateInput = date =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
        date.getDate(),
    )}`

const toDateParam = (value, endOfDay = false) => {
    if (!value) return undefined
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return undefined
    if (endOfDay) {
        date.setHours(23, 59, 59, 999)
    }
    return date.toISOString()
}

const getDefaultFilters = () => {
    const now = new Date()
    const from = new Date(now)
    from.setHours(0, 0, 0, 0)

    return {
        fromDate: toLocalDateInput(from),
        toDate: toLocalDateInput(now),
        companyId: null,
    }
}

const formatDuration = seconds => {
    if (seconds == null || seconds === '') return ''
    const total = Number(seconds)
    if (Number.isNaN(total)) return String(seconds)
    if (total < 60) return `${total}s`
    const mins = Math.floor(total / 60)
    const secs = total % 60
    if (mins < 60) return secs ? `${mins}m ${secs}s` : `${mins}m`
    const hours = Math.floor(mins / 60)
    const remMins = mins % 60
    return remMins ? `${hours}h ${remMins}m` : `${hours}h`
}

const formatWhenUtc = value => {
    if (!value) return ''
    const rawValue = String(value)
    const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(rawValue)
    const date = new Date(hasTimezone ? rawValue : `${rawValue}Z`)
    if (Number.isNaN(date.getTime())) return String(value)
    return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(
        date.getUTCDate(),
    )} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`
}

const getRowDisplay = row => {
    const status = rowValue(row, [
        'Status',
        'status',
        'EndReason',
        'endReason',
    ])
    const whenRaw = rowValue(row, [
        'StartedOn',
        'startedOn',
        'WhenUtc',
        'StartedAtUtc',
        'CreatedAtUtc',
        'startedAtUtc',
    ])
    const durationRaw = rowValue(
        row,
        [
            'DurationSeconds',
            'durationSeconds',
            'Duration',
            'DurationText',
            'duration',
        ],
        null,
    )
    const duration =
        typeof durationRaw === 'number' ||
        (typeof durationRaw === 'string' && /^\d+$/.test(durationRaw))
            ? formatDuration(durationRaw)
            : durationRaw || ''

    return {
        sessionId: rowValue(row, ['SessionId', 'sessionId', 'Id', 'id'], null),
        when: formatWhenUtc(whenRaw),
        actor: rowValue(row, [
            'ActorUserName',
            'ActorFullName',
            'ActorName',
            'ActorEmail',
            'Actor',
            'actorName',
        ]),
        targetUser: rowValue(row, [
            'TargetFullName',
            'TargetUserName',
            'TargetUserEmail',
            'TargetUser',
            'TargetName',
            'targetUserEmail',
        ]),
        targetCompany: rowValue(
            row,
            ['TargetCompanyName', 'CompanyName', 'targetCompanyName'],
            '—',
        ),
        duration,
        reason: rowValue(row, [
            'ReasonText',
            'reasonText',
            'Reason',
            'reason',
        ]),
        status,
        statusMeta: getStatusMeta(status),
    }
}

const HistoryCard = ({ item, forcingId, onForceEnd }) => {
    const display = getRowDisplay(item)

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                    {display.targetUser || 'Impersonation session'}
                </Card.Header>
                <Card.Description>
                    <CardMetaRow>
                        <span className="label">When (UTC)</span>
                        <span className="value">{display.when}</span>
                    </CardMetaRow>
                    <CardMetaRow>
                        <span className="label">Actor</span>
                        <span className="value">{display.actor}</span>
                    </CardMetaRow>
                    <CardMetaRow>
                        <span className="label">Target company</span>
                        <span className="value">{display.targetCompany}</span>
                    </CardMetaRow>
                    <CardMetaRow>
                        <span className="label">Duration</span>
                        <span className="value">{display.duration}</span>
                    </CardMetaRow>
                    <CardMetaRow>
                        <span className="label">Reason</span>
                        <span className="value">{display.reason}</span>
                    </CardMetaRow>
                    <CardMetaRow>
                        <span className="label">Status</span>
                        <span className="value">
                            <Label color={display.statusMeta.color} size="small">
                                {display.statusMeta.text}
                            </Label>
                        </span>
                    </CardMetaRow>
                    {isActiveStatus(display.status) && (
                        <FlexBox className="justify-content-end mt-2">
                            <Button
                                size="tiny"
                                color="red"
                                basic
                                loading={forcingId === display.sessionId}
                                onClick={() => onForceEnd(item)}
                            >
                                Force end
                            </Button>
                        </FlexBox>
                    )}
                </Card.Description>
            </Card.Content>
        </Card>
    )
}

// Screen 5 of the handoff: Super Admin-only audit of impersonation sessions.
export const ImpersonationHistoryScreen = () => {
    const { successToast, errorToast } = useNotification()
    const errorToastRef = useRef(errorToast)
    errorToastRef.current = errorToast
    const isMobile = useTabletAndBelowMediaQuery()
    const defaultFilters = useRef(getDefaultFilters()).current

    // Draft filters (UI only) — API uses appliedFilters after Apply / initial load.
    const [fromDate, setFromDate] = useState(defaultFilters.fromDate)
    const [toDate, setToDate] = useState(defaultFilters.toDate)
    const [companyId, setCompanyId] = useState(null)
    const [appliedFilters, setAppliedFilters] = useState(defaultFilters)

    const [rows, setRows] = useState([])
    const [totalCount, setTotalCount] = useState(0)
    const [pageNumber, setPageNumber] = useState(1)
    const [loading, setLoading] = useState(false)
    const [forcingId, setForcingId] = useState(null)

    const {
        options: companyOptions,
        loading: loadingCompanies,
        filterChanged: companyFilterChanged,
    } = GetAuditCompanyList(true, true, 50)

    const companyFilterOptions = useMemo(
        () => [{ key: 'any', value: null, text: 'Any' }, ...companyOptions],
        [companyOptions],
    )

    const fetchHistory = async (page = 1, filters = appliedFilters) => {
        setLoading(true)
        try {
            const queryParams = {
                fromUtc: toDateParam(filters.fromDate),
                toUtc: toDateParam(filters.toDate, true),
                targetCompanyId: filters.companyId || undefined,
                pageNumber: page,
                pageSize: PAGE_SIZE,
            }
            const res = await ImpersonationService.history(queryParams)
            const items = Array.isArray(res)
                ? res
                : res?.Rows ||
                  res?.rows ||
                  res?.Items ||
                  res?.items ||
                  []
            setRows(items)
            setTotalCount(
                res?.TotalCount ??
                    res?.totalCount ??
                    res?.TotalRecords ??
                    res?.totalRecords ??
                    items.length,
            )
            setPageNumber(page)
        } catch (error) {
            console.error('Error loading impersonation history:', error)
            errorToastRef.current('Failed to load impersonation history.')
        } finally {
            setLoading(false)
        }
    }

    // Initial load only; further loads are driven by Apply / pagination.
    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                const res = await ImpersonationService.history({
                    fromUtc: toDateParam(defaultFilters.fromDate),
                    toUtc: toDateParam(defaultFilters.toDate, true),
                    pageNumber: 1,
                    pageSize: PAGE_SIZE,
                })
                if (cancelled) return
                const items = Array.isArray(res)
                    ? res
                    : res?.Rows ||
                      res?.rows ||
                      res?.Items ||
                      res?.items ||
                      []
                setRows(items)
                setTotalCount(
                    res?.TotalCount ??
                        res?.totalCount ??
                        res?.TotalRecords ??
                        res?.totalRecords ??
                        items.length,
                )
                setPageNumber(1)
            } catch (error) {
                if (cancelled) return
                console.error('Error loading impersonation history:', error)
                errorToastRef.current('Failed to load impersonation history.')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    const handleApply = () => {
        const next = { fromDate, toDate, companyId }
        setAppliedFilters(next)
        fetchHistory(1, next)
    }

    const handleForceEnd = async row => {
        const sessionId = rowValue(
            row,
            ['SessionId', 'sessionId', 'Id', 'id'],
            null,
        )
        if (!sessionId) {
            errorToast('This session cannot be force-ended (missing id).')
            return
        }
        setForcingId(sessionId)
        try {
            await ImpersonationService.forceEnd(sessionId)
            successToast('Impersonation session force-ended.')
            fetchHistory(pageNumber, appliedFilters)
        } catch (error) {
            console.error('Error force-ending session:', error)
            errorToast('Failed to force-end the session.')
        } finally {
            setForcingId(null)
        }
    }

    const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * PAGE_SIZE + 1
    const rangeEnd = Math.min(pageNumber * PAGE_SIZE, totalCount)

    const emptyMessage = (
        <div
            style={{
                textAlign: 'center',
                padding: '24px 12px',
                color: '#6b7280',
            }}
        >
            No impersonation sessions found for these filters.
        </div>
    )

    let listContent = emptyMessage
    if (rows.length > 0 && isMobile) {
        listContent = (
            <CardList>
                {rows.map((row, index) => {
                    const key = rowValue(
                        row,
                        ['SessionId', 'sessionId', 'Id', 'id'],
                        `row-${index}`,
                    )
                    return (
                        <HistoryCard
                            key={key}
                            item={row}
                            forcingId={forcingId}
                            onForceEnd={handleForceEnd}
                        />
                    )
                })}
            </CardList>
        )
    } else if (rows.length > 0) {
        listContent = (
            <TableScroll>
                <Table celled selectable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>When (UTC)</Table.HeaderCell>
                        <Table.HeaderCell>Actor</Table.HeaderCell>
                        <Table.HeaderCell>Target user</Table.HeaderCell>
                        <Table.HeaderCell>Target company</Table.HeaderCell>
                        <Table.HeaderCell>Duration</Table.HeaderCell>
                        <Table.HeaderCell>Reason</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                        <Table.HeaderCell />
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {rows.map((row, index) => {
                        const display = getRowDisplay(row)
                        const sessionId = display.sessionId || `row-${index}`
                        return (
                            <Table.Row key={sessionId}>
                                <Table.Cell>{display.when}</Table.Cell>
                                <Table.Cell>{display.actor}</Table.Cell>
                                <Table.Cell>{display.targetUser}</Table.Cell>
                                <Table.Cell>{display.targetCompany}</Table.Cell>
                                <Table.Cell>{display.duration}</Table.Cell>
                                <Table.Cell>{display.reason}</Table.Cell>
                                <Table.Cell>
                                    <Label color={display.statusMeta.color}>
                                        {display.statusMeta.text}
                                    </Label>
                                </Table.Cell>
                                <Table.Cell textAlign="center">
                                    {isActiveStatus(display.status) && (
                                        <Button
                                            size="tiny"
                                            color="red"
                                            basic
                                            loading={
                                                forcingId === display.sessionId
                                            }
                                            onClick={() => handleForceEnd(row)}
                                        >
                                            Force end
                                        </Button>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        )
                    })}
                </Table.Body>
                </Table>
            </TableScroll>
        )
    }

    return (
        <div>
            <PageHeader header="Impersonation History" />

            <Filters $isMobile={isMobile}>
                <div className="field">
                    <label htmlFor="impersonation-from">From (UTC)</label>
                    <input
                        id="impersonation-from"
                        type="date"
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                    />
                </div>
                <div className="field">
                    <label htmlFor="impersonation-to">To (UTC)</label>
                    <input
                        id="impersonation-to"
                        type="date"
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                    />
                </div>
                <div className="field">
                    <label>Target Company</label>
                    <Dropdown
                        placeholder="Any"
                        selection
                        search
                        clearable
                        options={companyFilterOptions}
                        value={companyId}
                        loading={loadingCompanies}
                        onChange={(e, { value }) => setCompanyId(value || null)}
                        onSearchChange={(e, { searchQuery }) =>
                            companyFilterChanged({ search: searchQuery })
                        }
                    />
                </div>
                <div className="field" style={{ minWidth: 'auto' }}>
                    <AppPrimaryButton
                        type="button"
                        onClick={handleApply}
                        loading={loading}
                        fluid={isMobile}
                    >
                        Apply
                    </AppPrimaryButton>
                </div>
            </Filters>

            {loading ? (
                <LoaderBar content="Loading impersonation history..." />
            ) : (
                <>
                    {listContent}

                    <FooterRow>
                        <span>
                            Showing {rangeStart}-{rangeEnd} of {totalCount}
                        </span>
                        {totalCount > PAGE_SIZE && (
                            <PaginationComponent
                                activePage={pageNumber}
                                totalCount={totalCount}
                                pageSize={PAGE_SIZE}
                                pageChanged={page =>
                                    fetchHistory(page, appliedFilters)
                                }
                            />
                        )}
                    </FooterRow>
                </>
            )}
        </div>
    )
}
